import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { motion } from 'framer-motion';
import { teams } from '../data/competitionData';
import TeamRankCard from './TeamRankCard';
import ScoreBreakdown from './ScoreBreakdown';
import ParticleBackground from './ParticleBackground';
import AnimationController from './AnimationController';
import GlobalStyles from '../styles/GlobalStyles';
import theme from '../styles/theme';
import { fadeIn } from '../styles/animations';

// Styled components for the results showcase
const ShowcaseContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.md};
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  }
`;

const Header = styled(motion.header)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space['2xl']};
`;

const Title = styled.h1`
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto;
`;

const TeamsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.xl};
  margin: ${({ theme }) => theme.space.xl} 0;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-around;
  }
`;

const PodiumContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    justify-content: center;
    height: 500px; /* Increased from 400px to accommodate larger cards */
  }
`;

const PodiumStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 520px; /* Increased to match the new card width */
  margin-bottom: ${({ theme }) => theme.space.xl};
  position: relative;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    transform: translateY(-10px);
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    position: absolute;
    bottom: 0;
    margin-bottom: 0;
    
    ${({ position }) => {
      switch (position) {
        case 1:
          return `
            z-index: 3;
            left: 50%;
            transform: translate(-50%, -100px);
            
            &:hover {
              transform: translate(-50%, -110px);
            }
          `;
        case 2:
          return `
            right: 10%;
            z-index: 2;
            transform: translateY(-50px);
            
            &:hover {
              transform: translateY(-60px);
            }
          `;
        case 3:
          return `
            left: 10%;
            z-index: 1;
            
            &:hover {
              transform: translateY(-10px);
            }
          `;
        default:
          return '';
      }
    }}
  }
`;

// Podium base is now hidden as per user request
const PodiumBase = styled.div`
  display: none;
`;

const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 585px; /* Increased to match the new card width */
`;

const FourthPlaceSection = styled.div`
  margin-top: ${({ theme }) => theme.space['2xl']};
  width: 100%;
  max-width: 585px; /* Increased to match the new card width */
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 4; /* Ensure it's above background but below confetti */
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.space.md};
  text-align: center;
`;

const BreakdownSection = styled.div`
  margin-top: ${({ theme }) => theme.space['2xl']};
  max-width: 950px; /* Increased to accommodate wider content */
  width: 100%;
  align-self: center;
  position: relative;
  z-index: 4; /* Ensure it's above background but below confetti */
`;

const Footer = styled(motion.footer)`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.space['2xl']};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/**
 * ResultsShowcase Component
 * Main component that integrates all other components and manages the overall layout
 * Controls the step-by-step reveal animation flow
 */
const ResultsShowcase = () => {
  // Get sorted teams by rank
  const sortedTeams = [...teams].sort((a, b) => a.rank - b.rank);
  
  // Animation state
  const [animationState, setAnimationState] = useState({
    showFourthPlace: false,
    showThirdPlace: false,
    showSecondPlace: false,
    showFirstPlace: false,
    showCelebration: false,
    isComplete: false,
    isAnimating: false
  });
  
  // Function to update animation state to show 1st and 2nd place at the same time
  const handleAnimationStateChange = (newState) => {
    // If 1st place is shown, also show 2nd place
    if (newState.showFirstPlace && !newState.showSecondPlace) {
      newState.showSecondPlace = true;
    }
    
    setAnimationState(newState);
    
    // Set selected team based on animation state
    if (newState.showFirstPlace && !selectedTeam) {
      setSelectedTeam(sortedTeams.find(team => team.rank === 1));
    }
  };
  
  // Selected team for score breakdown
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  
  // Handle team card click to show score breakdown
  const handleTeamCardClick = (team) => {
    setSelectedTeam(team);
  };
  
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      
      {/* Background particles */}
      <ParticleBackground celebration={animationState.showCelebration} />
      
      {/* Animation controller */}
      <AnimationController 
        onAnimationStateChange={handleAnimationStateChange}
        autoStart={false}
      />
      
      <ShowcaseContainer>
        {/* Header */}
        <Header
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <img
            src="/baxter_logo.png"
            alt="Baxter Logo"
            style={{
              width: '180px',
              marginBottom: '20px',
              display: 'block',
              margin: '0 auto 20px auto'
            }}
          />
          <Title>AI Dojo Competition Results</Title>
          <Subtitle>
            Final standings from the 4-team competition showcasing AI trading platform development skills
          </Subtitle>
        </Header>
        
        {/* Podium for top 3 teams */}
        <PodiumContainer>
          {/* We need to render in specific order for proper layering */}
          {/* First render 2nd place (right side) */}
          {sortedTeams.find(team => team.rank === 2) && (
            <PodiumStep
              key={sortedTeams.find(team => team.rank === 2).id}
              position={2}
              onClick={() => handleTeamCardClick(sortedTeams.find(team => team.rank === 2))}
            >
              <TeamRankCard
                team={sortedTeams.find(team => team.rank === 2)}
                animate={true}
                visible={animationState.showSecondPlace}
              />
              <PodiumBase position={2} />
            </PodiumStep>
          )}
          
          {/* Then render 3rd place (left side) */}
          {sortedTeams.find(team => team.rank === 3) && (
            <PodiumStep
              key={sortedTeams.find(team => team.rank === 3).id}
              position={3}
              onClick={() => handleTeamCardClick(sortedTeams.find(team => team.rank === 3))}
            >
              <TeamRankCard
                team={sortedTeams.find(team => team.rank === 3)}
                animate={true}
                visible={animationState.showThirdPlace}
              />
              <PodiumBase position={3} />
            </PodiumStep>
          )}
          
          {/* Finally render 1st place (center, on top) */}
          {sortedTeams.find(team => team.rank === 1) && (
            <PodiumStep
              key={sortedTeams.find(team => team.rank === 1).id}
              position={1}
              onClick={() => handleTeamCardClick(sortedTeams.find(team => team.rank === 1))}
            >
              <TeamRankCard
                team={sortedTeams.find(team => team.rank === 1)}
                animate={true}
                visible={animationState.showFirstPlace}
              />
              <PodiumBase position={1} />
            </PodiumStep>
          )}
        </PodiumContainer>
        
        {/* 4th place team */}
        {sortedTeams.length > 3 && (
          <FourthPlaceSection>
            <SectionTitle>Honorable Mention</SectionTitle>
            <TeamSection onClick={() => handleTeamCardClick(sortedTeams[3])}>
              <TeamRankCard
                team={sortedTeams[3]}
                animate={true}
                visible={animationState.showFourthPlace}
              />
            </TeamSection>
          </FourthPlaceSection>
        )}
        
        {/* Score breakdown */}
        {selectedTeam && (
          <BreakdownSection>
            <ScoreBreakdown team={selectedTeam} animate={true} />
          </BreakdownSection>
        )}
        
        {/* Footer */}
        <Footer
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <p>AI Dojo Competition © {new Date().getFullYear()}</p>
        </Footer>
      </ShowcaseContainer>
    </ThemeProvider>
  );
};

export default ResultsShowcase;